import type { ListBuilderConfiguration } from '../../types';
import { adverseActionList } from './lists/adverse-action';
import { controlledNoDeaList } from './lists/controlled-no-dea';
import { deadLicenseList } from './lists/dead-license';
import { expiringSoonComplianceList } from './lists/expiring-soon';
import { linkEndedComplianceList } from './lists/link-ended';
import { noVerifiedLicenseList } from './lists/no-verified-license';
import { readyToConfirmList } from './lists/ready-to-confirm';
import { recentlyFixedList } from './lists/recently-fixed';
import { replacementAvailableList } from './lists/replacement-available';
import { wrongNumberList } from './lists/wrong-number';

/**
 * Compliance attention lists (epic PD-16252). Each dashboard card opens its own
 * list here — own columns, own locked (and visible) filters, ranked by the
 * account's 90-day sales — instead of a filtered copy of Current Licenses, where
 * a row never said why it was there. Rows are client_license records served by
 * the same `/licenses` endpoint (with `includeComplianceFacts`), so RBAC,
 * filters, counts and caching are the license list's own.
 */
export const LICENSE_COMPLIANCE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		controlledNoDeaList,
		deadLicenseList,
		adverseActionList,
		wrongNumberList,
		expiringSoonComplianceList,
		noVerifiedLicenseList,
		linkEndedComplianceList,
		readyToConfirmList,
		replacementAvailableList,
		recentlyFixedList,
	],
	paging: { defaultPageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
	showResetButton: true,
	// Every filter any list here locks must also be listed below, or the locked
	// value reaches the query and renders as a chip while the filter itself is
	// missing from the Filters panel — visible but neither inspectable nor
	// resettable. `attention-lists.test.ts` checks both directions.
	filters: {
		activeFilters: [
			'licenseKeywords',
			'licenseActiveBuyer',
			'licenseRegistryStanding',
			'licenseAdverseInForce',
			'licenseNumberCollision',
			'licenseControlledNoDea',
			'licenseHasReplacement',
			'licenseRecentlyFixed',
			'licenseRecentlyFixedWindow',
			'licenseClientCoverage',
			'licenseVerifiedExpiringSoon',
			'licenseLinkEnded',
			'licenseType',
			'licenseClientLocationState',
			'licenseClient',
			'licenseSalesRep',
		],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'sales-90-desc',
			name: 'Sales 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'clientRevenue90',
					objectTable: 'client_license',
				},
				{
					direction: 'ASC',
					fieldName: 'clientName',
					objectTable: 'client_license',
				},
			],
			description: 'Largest accounts first',
		},
		{
			id: 'sales-90-asc',
			name: 'Sales 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'clientRevenue90',
					objectTable: 'client_license',
				},
			],
			description: 'Smallest accounts first',
		},
	],
	objects: [
		{
			baseUrl: '/licenses/:id',
			objectLabel: 'License',
			objectTable: 'client_license',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'licenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'typeLabel', fieldLabel: 'Type' },
				{ fieldName: 'clientName', fieldLabel: 'Account' },
				{ fieldName: 'clientCityStateZip', fieldLabel: 'City/State/Zip' },
				{ fieldName: 'clientRevenue90', fieldLabel: 'Sales 90d' },
				{ fieldName: 'regulatorSummary', fieldLabel: 'Regulator' },
				{ fieldName: 'fileSummary', fieldLabel: 'Your File' },
				{ fieldName: 'whyHere', fieldLabel: "Why It's Here" },
			],
		},
	],
};
